import { useEffect, useState } from 'react';
import { Flex, Modal, TextArea, Text } from '@nula/core-ui';
import type { FeedbackVote } from './message-toolbar';

interface FeedbackModalProps {
  open: boolean;
  vote: FeedbackVote;
  onClose: () => void;
  /** Fires with the trimmed comment (empty string when no comment). */
  onSubmit: (comment: string) => void;
}

const UP_PLACEHOLDER = 'What do you think went well?';
const DOWN_PLACEHOLDER = 'What could be improved?';

/**
 * Feedback capture modal opened when the user clicks thumbs-up / thumbs-down
 * on an assistant message. Sends a single freeform comment back to nexus.
 *
 * No "include chat history" checkbox: cortex already records every trace in
 * LangSmith. The reviewer can pull the full transcript via the trace_id
 * (aligned with our run_id) at any time.
 */
export function FeedbackModal({
  open,
  vote,
  onClose,
  onSubmit,
}: FeedbackModalProps) {
  const [comment, setComment] = useState('');

  // Reset the comment whenever the modal is re-opened — otherwise the
  // previous submission's text leaks into the next message's feedback.
  useEffect(() => {
    if (open) setComment('');
  }, [open]);

  const handleSubmit = () => {
    onSubmit(comment.trim());
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send feedback"
      okText="Send feedback"
      cancelText="Cancel"
      onOk={handleSubmit}
      width={520}
      centered
    >
      <Flex vertical gap="md">
        <Text type="secondary" size="sm">
          We review all feedback to improve your experience with Nula
        </Text>
        <TextArea
          autoSize={{ minRows: 4, maxRows: 10 }}
          placeholder={vote === 'up' ? UP_PLACEHOLDER : DOWN_PLACEHOLDER}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
        />
      </Flex>
    </Modal>
  );
}
